import React, { useState, useRef, useEffect } from 'react';
import { fetchData } from '../../Utilities/handleRequest';
import {
  Container,
  Title,
  Form,
  InputGroup,
  Label,
  Input,
  Button,
  ButtonGroup,
  ErrorMessage,
  SuccessMessage,
  OtpContainer,
  OtpInput,
  WarningText,
  LogoContainer,
  ResendOtpText,
  MainContent
} from './styled';

const DeleteUser = () => {
  const [step, setStep] = useState(1);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState(['', '', '', '']); // 4 digits OTP
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [timer, setTimer] = useState(0);
  const otpRefs = useRef([]);

  useEffect(() => {
    // Set background when component mounts
    document.body.style.backgroundImage = `url(${process.env.PUBLIC_URL}/Home/home_page.svg)`;
    document.body.style.backgroundSize = 'cover';
    document.body.style.backgroundPosition = 'center';
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    document.body.style.minHeight = '100vh';

    // Cleanup when component unmounts
    return () => {
      document.body.style.backgroundImage = '';
      document.body.style.backgroundSize = '';
      document.body.style.backgroundPosition = '';
      document.body.style.margin = '';
      document.body.style.padding = '';
      document.body.style.minHeight = '';
    };
  }, []);

  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer((prevTimer) => prevTimer - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [timer]);

  // Reset success message when changing steps
  useEffect(() => {
    setSuccess('');
  }, [step]);

  const validatePhoneNumber = (phone) => {
    const phoneRegex = /^[0-9]{10}$/;
    return phoneRegex.test(phone);
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    if (value.length <= 10) {
      setPhoneNumber(value);
      setError('');
    }
  };

  const sendOtp = async () => {
    setError('');
    setLoading(true);

    try {
      const response = await fetchData('post', 'client-auth/send-otp', {
        phone_number: phoneNumber
      });

      if (response.error) {
        throw new Error(response.error?.data?.message || 'Failed to send OTP');
      }

      setStep(2);
      setSuccess('OTP sent successfully!');
      setTimer(60); // Start 60 seconds timer
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneSubmit = async (e) => {
    e.preventDefault();

    if (!phoneNumber) {
      setError('Phone number is required');
      return;
    }

    if (!validatePhoneNumber(phoneNumber)) {
      setError('Please enter a valid 10-digit phone number');
      return;
    }

    await sendOtp();
  };

  const handleOtpChange = (index, value) => {
    if (value.length > 1) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 3) { // Changed to 3 for 4 digits
      otpRefs.current[index + 1].focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1].focus();
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetchData('post', 'client-auth/validate-otp', {
        phone_number: phoneNumber,
        otp: otp.join('')
      });

      if (response.error) {
        throw new Error(response.error?.data?.message || 'Invalid OTP');
      }

      setStep(3);
      setSuccess('OTP verified successfully!');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConfirmation = async () => {
    setError('');
    setLoading(true);

    try {
      const response = await fetchData('post', 'admin-auth/user-delete-public', {
        phone_number: phoneNumber,
      });

      if (response.error) {
        throw new Error(response.error?.data?.message || 'Failed to delete account');
      }

      setSuccess('Your account has been successfully deleted.');
      localStorage.clear();
      setTimeout(() => {
        window.location.href = '/';
      }, 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    return `${Math.floor(seconds / 60)}:${(seconds % 60).toString().padStart(2, '0')}`;
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <Form onSubmit={handlePhoneSubmit} noValidate>
            <InputGroup>
              <Label>Phone Number</Label>
              <Input
                type="tel"
                value={phoneNumber}
                onChange={handlePhoneChange}
                placeholder="Enter your phone number"
                disabled={loading}
              />
              {phoneNumber && !validatePhoneNumber(phoneNumber) && (
                <ErrorMessage>Please enter a valid 10-digit phone number</ErrorMessage>
              )}
            </InputGroup>
            <ButtonGroup>
              <Button 
                type="submit" 
                variant="primary" 
                disabled={loading || !phoneNumber || !validatePhoneNumber(phoneNumber)}
              >
                {loading ? 'Sending OTP...' : 'Send OTP'}
              </Button>
            </ButtonGroup>
          </Form>
        );

      case 2:
        return (
          <Form onSubmit={handleOtpSubmit}>
            <Label>Enter OTP</Label>
            <OtpContainer>
              {otp.map((digit, index) => (
                <OtpInput
                  key={index}
                  ref={(el) => (otpRefs.current[index] = el)}
                  type="text"
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(index, e)}
                  pattern="[0-9]"
                  maxLength={1}
                  required
                />
              ))}
            </OtpContainer>
            {timer > 0 ? (
              <ResendOtpText>Resend OTP in {formatTime(timer)}</ResendOtpText>
            ) : (
              <ResendOtpText onClick={sendOtp} disabled={loading}>
                Resend OTP
              </ResendOtpText>
            )}
            <ButtonGroup>
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setStep(1);
                  setOtp(['', '', '', '']); // Reset OTP when going back
                }}
              >
                Back
              </Button>
              <Button 
                type="submit" 
                variant="primary" 
                disabled={loading || otp.some(digit => !digit)}
              >
                {loading ? 'Verifying...' : 'Verify OTP'}
              </Button>
            </ButtonGroup>
          </Form>
        );

      case 3:
        return (
          <>
            <WarningText>
              Are you sure you want to delete your account? This action cannot be undone.
            </WarningText>
            <ButtonGroup>
              <Button
                type="button"
                variant="secondary"
                onClick={() => setStep(2)}
              >
                Back
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={handleDeleteConfirmation}
                disabled={loading}
              >
                {loading ? 'Deleting...' : 'Confirm Delete'}
              </Button>
            </ButtonGroup>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Container>
      <MainContent>
        <LogoContainer>
          <img src={`${process.env.PUBLIC_URL}/bathx_logo.svg`} alt="BathX Logo" />
        </LogoContainer>
        <Title>Delete Account</Title>
        {error && <ErrorMessage>{error}</ErrorMessage>}
        {success && <SuccessMessage>{success}</SuccessMessage>}
        {renderStep()}
      </MainContent>
    </Container>
  );
};

export default DeleteUser;